import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Loader } from '../../components/Loader/Loader';
import { CustomDashboard } from '../../components/CustomDashboard/CustomDashboard';
import { SocialGraph } from '../SocialGraphPOC/SocialGraph';
import { artifactsApi, ArtifactBundleDetailDTO, ArtifactDTO, toSavedGraphDetail } from '../../utils/artifactsApi';
import { showRequestError } from '../../utils/requestError';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ARTIFACT_TYPE_LABELS, formatPublishedAt, getIsSuperAdmin, getWorkspaceRoles } from '../../utils/artifactsUi';
import { PdfViewer } from '../../components/PdfViewer/PdfViewer';
import { BundleManagePanel } from './BundleManagePanel';
import styles from './ArtifactBundlePage.module.scss';

export function ArtifactBundlePage() {
  const { t, i18n } = useTranslation();
  const { bundleId } = useParams<{ bundleId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bundle, setBundle] = useState<ArtifactBundleDetailDTO | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerFailed, setViewerFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // roles gate both the route and the fetches: a role-less 403 would wipe the
  // token and force a logout (API.fetch behavior)
  const [currentUser] = useCurrentUser();
  const workspaceRoles = getWorkspaceRoles(currentUser);
  const hasReadRole = Boolean(workspaceRoles?.includes('artifacts:read'));
  const canPublish = Boolean(getIsSuperAdmin(currentUser) && workspaceRoles?.includes('artifacts:publish'));

  // registry drill-in: ?ws= carries the bundle's workspace; sent only for super
  // admins (backend rejects the override from anyone else with a 403, which
  // API.fetch would turn into a forced logout)
  const wsParam = searchParams.get('ws');
  const workspaceId =
    getIsSuperAdmin(currentUser) && wsParam !== null && /^\d+$/.test(wsParam)
      ? Number(wsParam)
      : undefined;

  useEffect(() => {
    if (!hasReadRole || !bundleId) return;
    const controller = new AbortController();
    artifactsApi
      .getBundle(bundleId, { workspaceId, signal: controller.signal })
      .then((data) => setBundle(data))
      .catch((error) => showRequestError(error, t('Artifacts')));
    return () => controller.abort();
  }, [hasReadRole, bundleId, workspaceId, reloadKey, t]);

  const reloadBundle = useCallback(() => setReloadKey((key) => key + 1), []);

  const selectArtifact = useCallback(
    (artifactId: string) =>
      setSearchParams((params) => {
        params.set('item', artifactId);
        return params;
      }),
    [setSearchParams],
  );

  const artifacts = useMemo(
    () => (bundle ? [...bundle.artifacts].sort((a, b) => a.position - b.position) : []),
    [bundle],
  );

  const activeItemId = searchParams.get('item');
  const activeArtifact: ArtifactDTO | null =
    artifacts.find((artifact) => artifact.id === activeItemId) ?? artifacts[0] ?? null;
  const activeArtifactId = activeArtifact?.id;
  const isReport = activeArtifact?.type === 'REPORT';
  // reference-backed tabs (graph / dashboard) render the live platform object
  // through the artifact proxies; only file-backed ones go through the content host
  const activeRef = activeArtifact?.current_version?.ref ?? null;

  useEffect(() => {
    setViewerUrl(null);
    setViewerFailed(false);
    if (!hasReadRole || !bundleId || !activeArtifact || activeArtifact.type !== 'REPORT') return;
    const controller = new AbortController();
    artifactsApi
      .getContentUrl(bundleId, activeArtifact.id, 'inline', { workspaceId, signal: controller.signal })
      .then((url) => setViewerUrl(url))
      .catch((error) => {
        if ((error as Error)?.name !== 'AbortError') {
          setViewerFailed(true);
          showRequestError(error, t('Artifacts'));
        }
      });
    return () => controller.abort();
  }, [hasReadRole, bundleId, activeArtifact, workspaceId, t]);

  const loadGraph = useCallback(
    (signal?: AbortSignal) =>
      artifactsApi.getGraph(bundleId ?? '', activeArtifactId ?? '', { workspaceId, signal }).then(toSavedGraphDetail),
    [bundleId, activeArtifactId, workspaceId],
  );

  const dashboardApi = useMemo(
    () => artifactsApi.dashboardApi(bundleId ?? '', activeArtifactId ?? '', { workspaceId }),
    [bundleId, activeArtifactId, workspaceId],
  );

  const downloadArtifact = useCallback(async () => {
    if (!bundleId || !activeArtifact) return;
    try {
      const url = await artifactsApi.getContentUrl(bundleId, activeArtifact.id, 'attachment', {
        workspaceId,
      });
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.rel = 'noopener';
      anchor.click();
    } catch (error) {
      showRequestError(error, t('Artifacts'));
    }
  }, [bundleId, activeArtifact, workspaceId, t]);

  if (currentUser && !hasReadRole) return <Navigate to="/" replace />;

  if (bundle === null) {
    return (
      <div className="list-content">
        <Loader />
      </div>
    );
  }

  const isPdf = activeArtifact?.current_version?.content_type === 'application/pdf';

  return (
    <div className={cn('list-content', styles.page)}>
      <div className={styles.header}>
        <div className={cn('breadcrumb', styles.crumbs)}>
          <span>
            <Link to="/artifacts">{t('Artifacts')}</Link>
          </span>
          {workspaceId !== undefined && (
            <span>
              <Link to="/artifacts/registry">{t('Registry')}</Link>
            </span>
          )}
          <span>{bundle.title}</span>
        </div>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{bundle.title}</h1>
          {isReport && (
            <button type="button" className={styles.download} onClick={downloadArtifact}>
              {t('Download')}
            </button>
          )}
        </div>
        {canPublish && (
          <BundleManagePanel
            bundle={bundle}
            artifacts={artifacts}
            activeArtifact={activeArtifact}
            requestOptions={{ workspaceId }}
            onChanged={reloadBundle}
            onSelect={selectArtifact}
          />
        )}
        {bundle.description && <p className={styles.description}>{bundle.description}</p>}
        <div className={styles.meta}>
          {bundle.tags.map((tag) => (
            <span key={tag} className={styles.tag}>
              {tag}
            </span>
          ))}
          <span className={styles.published}>
            {t('Published')}: {formatPublishedAt(bundle.published_at, i18n.language)}
          </span>
        </div>
      </div>

      {artifacts.length > 1 && (
        <div className={styles.tabs}>
          {artifacts.map((artifact) => (
            <button
              key={artifact.id}
              type="button"
              className={cn(styles.tab, {
                [styles.tabActive]: activeArtifact?.id === artifact.id,
              })}
              onClick={() =>
                selectArtifact(artifact.id)
              }
            >
              <span className={styles.tabType}>{t(ARTIFACT_TYPE_LABELS[artifact.type])}</span>
              {artifact.title}
            </button>
          ))}
        </div>
      )}

      <div className={styles.viewer}>
        {activeArtifact?.type === 'SOCIAL_GRAPH' && activeRef && (
          <div key={activeArtifact.id} className={styles.graphTab}>
            <SocialGraph graphId={Number(activeRef.target_id)} graphSource={loadGraph} />
          </div>
        )}
        {activeArtifact?.type === 'DASHBOARD' && activeRef && (
          <div key={activeArtifact.id} className={styles.dashboardTab}>
            <CustomDashboard dashboardId={String(activeRef.target_id)} isAdmin={false} embedded api={dashboardApi} />
          </div>
        )}
        {!isReport && activeArtifact && !activeRef && (
          <div className="noData">{t('Failed to open artifact content')}</div>
        )}
        {isReport && viewerFailed && <div className="noData">{t('Failed to open artifact content')}</div>}
        {isReport && !viewerFailed && viewerUrl === null && <Loader />}
        {isReport && !viewerFailed && viewerUrl !== null && activeArtifact && isPdf && (
          <PdfViewer
            key={`${activeArtifact.id}-${viewerUrl}`}
            file={viewerUrl}
            onFailed={() => setViewerFailed(true)}
          />
        )}
        {isReport && !viewerFailed && viewerUrl !== null && activeArtifact && !isPdf && (
          <iframe
            key={`${activeArtifact.id}-${viewerUrl}`}
            className={styles.frame}
            title={activeArtifact.title}
            src={viewerUrl}
            sandbox="allow-scripts"
          />
        )}
      </div>
    </div>
  );
}
